import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CalendarX2,
  CheckCircle2,
  CircleAlert,
  ExternalLink,
  Lock,
  PencilLine,
  RefreshCw,
} from "lucide-react";

import apiClient from "../../../api-client";
import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import { DateTimePickerField } from "../../../components/DateTimePickerField";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { useAuth } from "../../../components/contexts/AuthContext";
import { brand } from "../../../theme/theme";
import {
  cancelPieceSchedule,
  createConnectLink,
  getClientSocial,
  publishingErrorText,
  retryPiecePublication,
  schedulePiece,
  syncClientSocial,
  updatePieceScheduledAt,
} from "../../../services/socialPublishingService";
import { ConnectWarning } from "../../Clients/social/ConnectWarning";
import {
  consumePortalOpened,
  markPortalOpened,
  readPortalReturn,
} from "../../Clients/social/portalReturn";
import { piecePath } from "../piece/ContentPiecePage";
import { BusyNote, ButtonSpinner } from "./BusyIndicator";
import { cardShellSx } from "./ContentPreviewCard";
import {
  PlatformIcon,
  adaptationFormat,
  isPublishablePlatform,
  isVideoExpiredError,
  mediaWarnings,
  platformLabel,
} from "./platformMeta";
import { sidebarGridColumns } from "./ReviewView";
import { formatDateTimeInputValue } from "./workflowUtils";

const POST_STATUS = {
  scheduled: { label: "Programado", color: "info" },
  review: { label: "En revisión", color: "pending" },
  published: { label: "Publicado", color: "done" },
  error: { label: "Error", color: "blocked" },
  cancelled: { label: "Cancelado", color: "muted" },
};

const formatLongDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function Banner({ tone, icon: Icon, title, children }) {
  const tones = {
    muted: { bg: "#F8FAFC", border: "#E2E8F0", color: "#334155" },
    info: { bg: "#EFF4FE", border: "#D3E0FB", color: "#1D4ED8" },
    done: { bg: "#E3F6EA", border: "#C4E8D2", color: "#0F9D58" },
    error: { bg: "#FEF2F2", border: "#FBD5D5", color: "#B91C1C" },
    brand: { bg: brand.soft, border: brand.softBorder, color: brand.pinkDark },
  };
  const palette = tones[tone] || tones.muted;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.25,
        p: 1.75,
        borderRadius: "12px",
        border: `1px solid ${palette.border}`,
        backgroundColor: palette.bg,
      }}
    >
      <Box sx={{ color: palette.color, mt: "1px" }}>
        <Icon size={18} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: palette.color }}>{title}</Typography>
        {children && (
          <Typography component="div" sx={{ fontSize: "0.8125rem", color: "#475569", mt: 0.25 }}>
            {children}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const timeLabel = (date) =>
  date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

/** Right-hand date of a queue row: "en 42 min" / "hoy" / "4 sep", plus the time. */
const queueWhen = (value) => {
  if (!value) return { main: "Sin fecha", sub: "", urgent: false };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { main: "Sin fecha", sub: "", urgent: false };
  const now = new Date();
  const minutes = Math.round((date.getTime() - now.getTime()) / 60000);
  const isToday = date.toDateString() === now.toDateString();
  if (minutes >= 0 && minutes < 60) {
    return { main: `en ${Math.max(1, minutes)} min`, sub: `hoy ${timeLabel(date)}`, urgent: true };
  }
  return {
    main: isToday ? "hoy" : date.toLocaleDateString("es-ES", { day: "numeric", month: "short" }).replace(".", ""),
    sub: timeLabel(date),
    urgent: false,
  };
};

function QueueRow({ item, onOpen }) {
  const when = queueWhen(item.date);
  return (
    <Box
      onClick={onOpen}
      sx={{
        px: 2.5,
        py: 1.75,
        borderBottom: "1px solid #F1F5F9",
        "&:last-of-type": { borderBottom: "none" },
        cursor: onOpen ? "pointer" : "default",
        "&:hover": onOpen ? { backgroundColor: "#F8FAFC" } : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <PlatformIcon platform={item.platform} size={32} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}>
            {item.title}
          </Typography>
          {item.error ? (
            <Typography noWrap sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#B91C1C" }}>
              {platformLabel(item.platform)} · Error al publicar
            </Typography>
          ) : (
            <Typography noWrap sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
              {platformLabel(item.platform)} · {item.statusLabel}
            </Typography>
          )}
        </Box>
        {item.permalink ? (
          <Box
            component="a"
            href={item.permalink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            sx={{ color: brand.pink, fontWeight: 700, fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}
          >
            Ver <ExternalLink size={12} />
          </Box>
        ) : (
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.8125rem", color: when.urgent ? brand.pink : "#111827" }}>
              {when.main}
            </Typography>
            {when.sub && <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>{when.sub}</Typography>}
          </Box>
        )}
      </Box>
      {item.error && <AlertNote icon={CircleAlert} sx={{ mt: 1.25, p: 1.25 }}>{item.error}</AlertNote>}
    </Box>
  );
}

/** Exact date + time (15-min slots) picker with its label and hint. */
function DateTimeField({ label, value, onChange, disabled, hint, info, disablePast = false }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>{label}</Typography>
        {/* Above UnifiedModal (z-index 100000), where this field is also used. */}
        {info && <InfoTooltip title={info} placement="top" size={14} zIndex={100001} />}
      </Box>
      <DateTimePickerField value={value} onChange={onChange} disabled={disabled} disablePast={disablePast} />
      {hint && <Typography sx={{ fontSize: "0.75rem", color: "#B45309" }}>{hint}</Typography>}
    </Box>
  );
}

const CONNECTION_TONES = {
  connected: { background: "#E3F6EA", color: "#0F9D58" },
  pending: { background: "#FEF8EC", color: "#B45309" },
};

/** Connection state of one publishable network of this piece; connect button while not connected. */
function ConnectionRow({ platform, account, onConnect, busy }) {
  const connected = account?.status === "connected";
  const needsReconnect = account?.status === "needs_reconnect";
  const tone = connected ? "connected" : "pending";
  const accountName = account?.channel_name || account?.display_name || account?.username;
  let detail;
  if (connected) detail = accountName ? `Conectado · ${accountName}` : "Conectado";
  else if (needsReconnect) detail = account?.last_error || "Hay que volver a conectarlo";
  else detail = "Sin conectar: no se programará";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: "12px",
        backgroundColor: CONNECTION_TONES[tone].background,
      }}
    >
      <PlatformIcon platform={platform} size={30} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#111827" }}>
          {platformLabel(platform)}
        </Typography>
        <Typography noWrap title={detail} sx={{ fontSize: "0.75rem", color: CONNECTION_TONES[tone].color }}>
          {detail}
        </Typography>
      </Box>
      {!connected && (
        <Button size="sm" onClick={onConnect} disabled={busy} sx={{ borderRadius: "999px", backgroundColor: "#111827", "&:hover": { backgroundColor: "#1E293B" } }}>
          {busy ? <ButtonSpinner size={14} /> : null}
          {needsReconnect ? "Reconectar" : "Conectar"}
        </Button>
      )}
    </Box>
  );
}

/**
 * Step 4 — date of the piece and its bundle.social post, network by network.
 * The whole piece is scheduled at once in every connected network; the blog and
 * any other non-publishable network are left out. `preview` (the step 2 cards)
 * fills the right column; a scheduled piece changes its date or is unscheduled
 * from a modal.
 */
export function SchedulingView({
  piece,
  adaptations,
  preview,
  isClient,
  clientId,
  tutorialSocial = null,
  onPieceChanged,
  onPublishError,
  notify,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { interceptorReady } = useAuth();
  const [draftDate, setDraftDate] = useState(formatDateTimeInputValue(piece?.scheduled_at));
  const [busyAction, setBusyAction] = useState(null);
  const [actionError, setActionError] = useState("");
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connecting, setConnecting] = useState(null); // platform being sent to the portal
  const [confirmFor, setConfirmFor] = useState(null); // platform awaiting the connect warning
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [retryMode, setRetryMode] = useState(false); // date modal used to pick the retry date
  const [modalDate, setModalDate] = useState("");
  const handledReturnRef = useRef(false);

  useEffect(() => {
    setDraftDate(formatDateTimeInputValue(piece?.scheduled_at));
  }, [piece?.id, piece?.scheduled_at]);

  const { data: fetchedSocial, isLoading: isLoadingSocial } = useQuery({
    queryKey: ["client-social", clientId],
    queryFn: () => getClientSocial(clientId),
    enabled: interceptorReady && !isClient && Boolean(clientId),
    staleTime: 60 * 1000,
  });
  const social = tutorialSocial ?? fetchedSocial;

  // Every scheduled adaptation of the client, for the publication queue.
  const configId = piece?.config;
  const { data: calendarResponse } = useQuery({
    queryKey: ["scheduling-calendar", configId, piece?.scheduled_at],
    queryFn: async () => {
      const response = await apiClient.get("/content-generation/adaptations/", {
        params: { config: configId, scheduled: "true" },
      });
      return response.data;
    },
    enabled: interceptorReady && Boolean(configId),
    staleTime: 60 * 1000,
  });

  const accountsByPlatform = useMemo(() => {
    const map = {};
    (social?.accounts || []).forEach((account) => {
      map[account.platform] = account;
    });
    return map;
  }, [social]);

  const approved = piece?.status === "client_approved";
  const publication = piece?.publication_status || "none";
  const hasErrors = Boolean(piece?.has_publication_errors);
  const publishable = adaptations.filter((a) => isPublishablePlatform(a.platform));
  // The blog is published by hand on the web: its date is only saved, for the client and the calendar.
  const isBlogOnly = publishable.length === 0 && adaptations.some((a) => a.platform === "blog");
  const connectedPublishable = publishable.filter(
    (a) => accountsByPlatform[a.platform]?.status === "connected"
  );

  // Same checks as the step 1 card: the networks would reject these files.
  const mediaProblems = connectedPublishable.flatMap((a) =>
    mediaWarnings(a.platform, adaptationFormat(a), a.images || []).map((warning) => ({
      key: `${a.id}-${warning}`,
      text: `${platformLabel(a.platform)}: ${warning}`,
    }))
  );

  const originalDate = formatDateTimeInputValue(piece?.scheduled_at);
  const draftIsFuture = draftDate && new Date(draftDate).getTime() > Date.now();

  // Connect straight from here: bundle.social's portal sends the user back to this piece.
  const openPortal = async (platform) => {
    setConnecting(platform);
    setActionError("");
    try {
      const url = await createConnectLink(clientId, location.pathname);
      markPortalOpened(clientId);
      window.location.assign(url);
    } catch (err) {
      setActionError(publishingErrorText(err, "No se ha podido abrir la conexión."));
      setConnecting(null);
    }
  };

  const connectNetwork = (platform) => {
    if (!clientId) return;
    setConfirmFor(platform);
  };

  // Back from the portal: report what happened, refresh the connections and clean the URL.
  useEffect(() => {
    if (isClient || !interceptorReady || !clientId || handledReturnRef.current) return;
    const results = readPortalReturn(searchParams);
    const returned = consumePortalOpened(clientId);
    if (!results.length && !returned) return;
    handledReturnRef.current = true;
    if (results.length) {
      const next = new URLSearchParams(searchParams);
      results.forEach((result) => next.delete(result.key));
      setSearchParams(next, { replace: true });
    }
    const failure = results.find((result) => result.severity === "error");
    if (failure) setActionError(failure.message);
    syncClientSocial(clientId)
      .then((updated) => {
        queryClient.setQueryData(["client-social", clientId], updated);
        if (!failure) notify?.("Conexiones actualizadas");
      })
      .catch((err) => setActionError(publishingErrorText(err, "No se han podido comprobar las conexiones.")));
  }, [isClient, interceptorReady, clientId, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Resolves true when the action succeeded. */
  const run = async (key, action, successMessage) => {
    setBusyAction(key);
    setActionError("");
    try {
      await action();
      onPublishError?.(null);
      notify?.(successMessage);
      queryClient.invalidateQueries({ queryKey: ["client-social", clientId] });
      await onPieceChanged?.();
      return true;
    } catch (err) {
      const message = publishingErrorText(err, "No se ha podido completar la acción.");
      // The card of the network marks the file at fault (e.g. a video that expired in bundle.social).
      onPublishError?.(err);
      setActionError(
        err?.response?.data?.media_id != null || isVideoExpiredError(message)
          ? `${message}\nEl archivo está marcado en la tarjeta de su red.`
          : message
      );
      notify?.(message, true);
      return false;
    } finally {
      setBusyAction(null);
    }
  };

  const toIso = (value) => (value ? new Date(value).toISOString() : null);

  const handleSchedule = () =>
    run(
      "schedule",
      () => schedulePiece(piece.id, toIso(draftDate)),
      `Programado en ${connectedPublishable.length} ${connectedPublishable.length === 1 ? "red" : "redes"}`
    );

  const handleSaveBlogDate = () =>
    run("date", () => updatePieceScheduledAt(piece.id, toIso(draftDate)), "Fecha de publicación guardada");

  const openDateModal = (retry = false) => {
    // The failed date is already past: a retry needs a new one.
    setModalDate(retry ? "" : originalDate);
    setRetryMode(retry);
    setActionError("");
    setDateModalOpen(true);
  };

  const closeDateModal = () => {
    if (busyAction) return;
    setDateModalOpen(false);
    setActionError("");
  };

  const handleMoveSchedule = async () => {
    const ok = await run(
      "date",
      () => updatePieceScheduledAt(piece.id, toIso(modalDate)),
      "Fecha de programación actualizada"
    );
    if (ok) setDateModalOpen(false);
  };

  const handleCancel = async () => {
    const ok = await run("cancel", () => cancelPieceSchedule(piece.id), "Programación cancelada");
    if (ok) setDateModalOpen(false);
  };

  const modalDateIsFuture = modalDate && new Date(modalDate).getTime() > Date.now();

  const handleRetry = async () => {
    const ok = await run(
      "retry",
      () => retryPiecePublication(piece.id, toIso(modalDate)),
      "Publicación reprogramada"
    );
    if (ok) setDateModalOpen(false);
  };

  // ── Publication queue: this piece's networks + the client's upcoming posts ──
  const openPiece = (pieceId, adaptationId) => {
    if (!pieceId || String(pieceId) === String(piece?.id)) return;
    navigate(`${piecePath(isClient, pieceId)}${adaptationId ? `?adaptation=${adaptationId}` : ""}`);
  };

  const queueItems = useMemo(() => {
    const now = Date.now();
    const toItem = (adaptation, isCurrent) => {
      const post = adaptation.social_post;
      const publishableNetwork = isPublishablePlatform(adaptation.platform);
      const date = post?.post_date || adaptation.scheduled_at;
      return {
        key: `${isCurrent ? "current" : "other"}-${adaptation.id}`,
        pieceId: isCurrent ? piece?.id : adaptation.content_piece_id ?? adaptation.content_piece,
        adaptationId: adaptation.id,
        isCurrent,
        platform: adaptation.platform,
        title: isCurrent
          ? piece?.title || "Esta pieza"
          : adaptation.content_piece_title || adaptation.title || "Contenido",
        date,
        statusLabel: publishableNetwork
          ? (POST_STATUS[post?.status] || { label: "Sin programar" }).label
          : "No se programa",
        error: post?.status === "error" ? post.error_message || "Error al publicar" : "",
        permalink: post?.status === "published" ? post.permalink : "",
      };
    };

    // Only what is actually in bundle.social's queue: a post with a date (not the unsaved draft date).
    const current = adaptations
      .filter((a) => a.social_post?.post_date && a.social_post.status !== "cancelled")
      .map((a) => toItem(a, true));
    const others = (calendarResponse?.results || (Array.isArray(calendarResponse) ? calendarResponse : []))
      .filter((a) => String(a.content_piece_id ?? a.content_piece) !== String(piece?.id))
      .filter((a) => a.social_post?.status !== "published" && a.social_post?.status !== "cancelled")
      .map((a) => toItem(a, false))
      .filter((item) => item.date && new Date(item.date).getTime() >= now);

    const time = (item) => (item.date ? new Date(item.date).getTime() : -Infinity);
    return [...current, ...others].sort((a, b) => time(a) - time(b));
  }, [adaptations, calendarResponse, piece]);

  const upcomingCount = queueItems.filter((item) => !item.isCurrent).length;

  // ── Summary banner ──
  let banner;
  if (isBlogOnly) {
    banner = piece?.scheduled_at ? (
      <Banner tone="info" icon={CalendarClock} title={`Publicación prevista el ${formatLongDate(piece.scheduled_at)}`}>
        El blog se publica en la web en esta fecha.
      </Banner>
    ) : (
      <Banner tone="brand" icon={CalendarClock} title="Sin fecha de publicación">
        {isClient
          ? "El equipo indicará cuándo se publicará el blog."
          : "El blog no se programa automáticamente: indica cuándo se publicará para que el cliente lo sepa."}
      </Banner>
    );
  } else if (!approved && publication === "none") {
    banner = (
      <Banner tone="muted" icon={Lock} title="Programación bloqueada">
        Se podrá programar cuando el cliente apruebe el contenido.
      </Banner>
    );
  } else if (publication === "published") {
    banner = (
      <Banner tone="done" icon={CheckCircle2} title="Publicado">
        Todas las redes programadas se han publicado.
      </Banner>
    );
  } else if (publication === "scheduled") {
    banner = hasErrors ? (
      <Banner tone="error" icon={AlertTriangle} title="Alguna red ha fallado">
        Revisa el error de cada red, corrígelo y reprograma las que han fallado.
      </Banner>
    ) : (
      <Banner tone="info" icon={CalendarClock} title={`Programado para el ${formatLongDate(piece?.scheduled_at)}`}>
        Si editas el contenido, también se actualiza en las redes.
      </Banner>
    );
  } else {
    banner = (
      <Banner tone="brand" icon={CalendarClock} title="Listo para programar">
        {isClient
          ? "El equipo lo programará en tus redes."
          : "Elige la fecha y se programará a la vez en todas las redes conectadas."}
      </Banner>
    );
  }

  // Unscheduled: the date is picked inline. Scheduled: from the "Editar fecha" modal.
  const canPickDate = !isClient && publication === "none";
  const isScheduled = publication === "scheduled";
  const isBusy = Boolean(busyAction);
  const busyMessage = {
    schedule: "Programando en las redes…",
    date: "Cambiando la fecha de publicación…",
    cancel: "Cancelando la programación…",
    retry: "Reprogramando las redes que han fallado…",
  }[busyAction];
  const multiPlatformInfo =
    adaptations.length > 1
      ? "Todas las adaptaciones de esta pieza se publicarán en sus plataformas a la misma hora. Si quieres publicarlas en momentos distintos, genera contenidos individuales separados."
      : "";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: sidebarGridColumns,
        gap: 2,
        alignItems: "start",
        maxWidth: { md: 1440 }, // same as the stepper
      }}
    >
      {/* ── Right (first on mobile): the piece's networks, as in step 2 ── */}
      <Box sx={{ minWidth: 0 }}>{preview}</Box>

      <Box
        data-tutorial="cpm-scheduling"
        sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, order: { lg: -1 }, position: { lg: "sticky" }, top: { lg: 0 } }}
      >
        {/* ── Date and actions ── */}
        <Box sx={{ ...cardShellSx, p: 2.5, gap: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.0625rem", color: "#0F172A" }}>Programación</Typography>
          {banner}

          {approved && canPickDate && mediaProblems.length > 0 && (
            <Banner tone="error" icon={AlertTriangle} title="Corrige esto antes de programar">
              {mediaProblems.length === 1 ? (
                mediaProblems[0].text
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {mediaProblems.map((problem) => (
                    <li key={problem.key}>{problem.text}</li>
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 0.5, fontSize: "0.75rem", color: "#6B7280" }}>Cámbialo en el paso 1, en la tarjeta de su red.</Box>
            </Banner>
          )}

          {canPickDate && (
            <DateTimeField
              label="Fecha y hora de publicación"
              value={draftDate}
              onChange={setDraftDate}
              disabled={isBusy}
              disablePast
              info={multiPlatformInfo}
              hint={draftDate && !draftIsFuture ? "La fecha tiene que ser futura para programar." : ""}
            />
          )}

          {publication === "published" && piece?.scheduled_at && (
            <Tooltip title="No es posible editar la fecha de programación, el contenido ya ha sido publicado." placement="top" arrow>
              <Box sx={{ cursor: "not-allowed", "& input": { pointerEvents: "none" } }}>
                <DateTimeField
                  label="Fecha programada"
                  value={originalDate}
                  onChange={() => {}}
                  disabled
                />
              </Box>
            </Tooltip>
          )}

          {!isClient && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {isBlogOnly && canPickDate && (
                <Button
                  onClick={handleSaveBlogDate}
                  disabled={isBusy || !draftIsFuture || draftDate === originalDate}
                >
                  {busyAction === "date" ? <ButtonSpinner /> : <CalendarClock size={16} />}
                  Guardar fecha de publicación
                </Button>
              )}
              {!isBlogOnly && approved && canPickDate && (
                <Button
                  onClick={handleSchedule}
                  disabled={isBusy || !draftIsFuture || connectedPublishable.length === 0 || mediaProblems.length > 0}
                >
                  {busyAction === "schedule" ? <ButtonSpinner /> : <CalendarClock size={16} />}
                  {connectedPublishable.length > 0
                    ? `Programar en ${connectedPublishable.length} ${connectedPublishable.length === 1 ? "red" : "redes"}`
                    : "Programar"}
                </Button>
              )}
              {busyMessage && !dateModalOpen && <BusyNote message={busyMessage} />}
              {isScheduled && !hasErrors && (
                <Button variant="outline" onClick={() => openDateModal()} disabled={isBusy}>
                  <PencilLine size={16} />
                  Editar fecha de programación
                </Button>
              )}
              {hasErrors && (
                <Button variant="outline" onClick={() => openDateModal(true)} disabled={isBusy}>
                  <RefreshCw size={16} />
                  Reintentar publicación
                </Button>
              )}
              {(clientId || tutorialSocial) && publication !== "published" && publishable.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#374151" }}>
                    Conexiones necesarias para esta publicación:
                  </Typography>
                  {isLoadingSocial ? (
                    <CircularProgress size={18} />
                  ) : (
                    publishable.map((a) => (
                      <ConnectionRow
                        key={a.platform}
                        platform={a.platform}
                        account={accountsByPlatform[a.platform]}
                        busy={Boolean(connecting)}
                        onConnect={() => connectNetwork(a.platform)}
                      />
                    ))
                  )}
                </Box>
              )}
              {actionError && !dateModalOpen && (
                <Typography sx={{ fontSize: "0.8125rem", color: "error.main", whiteSpace: "pre-line" }}>{actionError}</Typography>
              )}
            </Box>
          )}
        </Box>

        {/* ── Publication queue ── */}
        <Box sx={cardShellSx}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, borderBottom: "1px solid #F1F5F9" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>Cola de programación</Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
              {upcomingCount > 0 ? `próximas ${upcomingCount}` : "sin próximas"}
            </Typography>
          </Box>
          <Box sx={{ maxHeight: 520, overflowY: "auto" }}>
            {queueItems.length === 0 && (
              <Typography sx={{ px: 2.5, py: 3, fontSize: "0.875rem", color: "#9CA3AF", textAlign: "center" }}>
                No hay publicaciones programadas.
              </Typography>
            )}
            {queueItems.map((item) => (
              <QueueRow
                key={item.key}
                item={item}
                onOpen={item.isCurrent ? undefined : () => openPiece(item.pieceId, item.adaptationId)}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <UnifiedModal
        open={dateModalOpen}
        onClose={closeDateModal}
        title={retryMode ? "Reintentar publicación" : "Editar fecha de programación"}
        maxWidth="xs"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontSize: "0.875rem", color: "#475569" }}>
            {retryMode ? (
              <>
                Las redes que han fallado se programarán juntas en la nueva fecha. Las que ya se
                publicaron no se tocan.
              </>
            ) : (
              <>
                Programado para el <strong>{formatLongDate(piece?.scheduled_at)}</strong>. Elige una nueva fecha
                o cancela la programación: el contenido no se publicará hasta que lo vuelvas a programar.
              </>
            )}
          </Typography>
          <DateTimeField
            label="Nueva fecha y hora"
            value={modalDate}
            onChange={setModalDate}
            disabled={isBusy}
            disablePast
            info={multiPlatformInfo}
            hint={modalDate && !modalDateIsFuture ? "La fecha tiene que ser futura." : ""}
          />
          {actionError && (
            <Typography sx={{ fontSize: "0.8125rem", color: "error.main", whiteSpace: "pre-line" }}>{actionError}</Typography>
          )}
          {busyMessage && <BusyNote message={busyMessage} />}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
            <Button variant="outline" onClick={handleCancel} disabled={isBusy} sx={{ color: "error.main" }}>
              {busyAction === "cancel" ? <ButtonSpinner /> : <CalendarX2 size={16} />}
              Cancelar programación
            </Button>
            {retryMode ? (
              <Button onClick={handleRetry} disabled={isBusy || !modalDateIsFuture}>
                {busyAction === "retry" ? <ButtonSpinner /> : <RefreshCw size={16} />}
                Reintentar
              </Button>
            ) : (
              <Button
                onClick={handleMoveSchedule}
                disabled={isBusy || !modalDateIsFuture || modalDate === originalDate}
              >
                {busyAction === "date" ? <ButtonSpinner /> : null}
                Guardar nueva fecha
              </Button>
            )}
          </Box>
        </Box>
      </UnifiedModal>
      <ConnectWarning
        open={Boolean(confirmFor)}
        platforms={confirmFor ? [confirmFor] : []}
        onCancel={() => setConfirmFor(null)}
        onConfirm={() => {
          const platform = confirmFor;
          setConfirmFor(null);
          openPortal(platform);
        }}
      />
    </Box>
  );
}
